// Getting edits to the server (ADR 0011). Each entry is either acknowledged (committed in D1 with
// its receipt before the reply), pending (kept in this account's IndexedDB for this show), or a
// proposal the server refused and kept here for review. Nothing is silently dropped.
//
// One request in flight per tab; retries with backoff from 1 s to 30 s, and at once when the
// network, the tab or the live connection comes back. Entries carry ids, so a retry after a lost
// reply is answered "duplicate" instead of being applied twice.

import { loadDoc } from "../format/load.ts";
import { applyOp, type Op } from "../format/ops.ts";
import type { AnyRecord, Doc, RecordType } from "../format/types.ts";
import { ApiError, AuthError, ConnectionError, api, post } from "./account.ts";
import { clearHistory } from "./edits.ts";
import { allPending, allProposals, deletePending, moveToProposals, type PendingEntry, type Proposal, putPending, readSnapshot, retagPending, saveSnapshot, type ShowSnapshot } from "./idb.ts";
import { prepareOffline, snapshotUnavailable } from "./offline.ts";
import { dialog, notify, pending, presence, proposals, serverDoc, show, status, tabId } from "./state.ts";

export { AuthError } from "./account.ts";

interface ShowResponse {
  accountId: string;
  show: { id: string; title: string; state: ShowSnapshot["state"]; role: ShowSnapshot["role"]; revision: number; generation: number; sourceSha256: string };
  doc: Doc;
}

let connected = false;
let refreshing: Promise<void> | undefined;
let refreshAgain = false;

function snapshot(): ShowSnapshot {
  const s = show.value;
  return { showId: s.showId, accountId: s.accountId, title: s.title, doc: serverDoc.value, revision: s.revision, generation: s.generation, state: s.state, role: s.role, sourceSha256: s.sourceSha256, savedAt: Date.now() };
}

function lockFor(state: ShowSnapshot["state"], role: ShowSnapshot["role"]): string {
  if (state === "archived") return "This show is archived, so it can't be changed. Its owner can unarchive it.";
  if (state === "trashed") return "This show has been deleted. Its owner can restore it from the trash.";
  if (role === "viewer") return "You're a viewer of this show, so you can't change it.";
  return "";
}

async function remember(seqs: number[] = []): Promise<void> {
  try {
    await saveSnapshot(snapshot(), seqs);
    void prepareOffline(show.value);
  } catch (error) {
    // Never delete acknowledged queue entries unless their new base is durable too.
    snapshotUnavailable(error);
  }
}

/** Retry snapshot persistence as well as app/PDF preparation after a storage/network failure. */
export function retryOffline(): void {
  void remember();
}

function adopt(r: ShowResponse): void {
  const before = show.value;
  serverDoc.value = r.doc;
  show.value = {
    ...before,
    title: r.show.title,
    revision: r.show.revision,
    generation: r.show.generation,
    state: r.show.state,
    role: r.show.role,
    sourceSha256: r.show.sourceSha256,
    lock: lockFor(r.show.state, r.show.role),
  };
  if (before.generation && r.show.generation !== before.generation) {
    clearHistory();
    notify("The owner restored this show to an earlier version. Anything you hadn't sent before that is held for your review.", "warn", undefined, 15000);
  }
}

let sending = false;
/** Set once this tab must never send again (another account took over the browser). */
let stopped = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let backoff = 1000;

/** Send whatever is pending, now (cancels a scheduled retry). */
export function kick(): void {
  clearTimeout(retryTimer);
  retryTimer = undefined;
  void pump();
}

function scheduleRetry(): void {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(kick, backoff);
  backoff = Math.min(backoff * 2, 30_000);
}

function updateStatus(): void {
  if (status.value.kind === "error") return;
  const waiting = pending.value.filter((e) => !e.held).length;
  if (!connected) status.value = { kind: "offline", message: "Can't reach q2cue" };
  else if (!waiting) status.value = { kind: "saved" };
  else status.value = { kind: "saving" };
}
pending.subscribe(updateStatus);

type Result =
  | { id: string; status: "accepted" | "duplicate"; revision: number }
  | { id: string; status: "conflict"; reason: "stale" | "reference"; message: string; records: Proposal["records"] }
  | { id: string; status: "invalid"; problems: string[] }
  | { id: string; status: "held"; reason: "restored"; message: string; generation: number };

/** Move refused entries into review, out of the queue, and tell the person once. */
async function refuse(items: Proposal[]): Promise<void> {
  if (!items.length) return;
  const seqs = await moveToProposals(items).catch(() => [] as number[]);
  const refused = new Set(items.map((p) => p.entry));
  pending.value = pending.value.filter((e) => !refused.has(e));
  proposals.value = [...proposals.value, ...items.map((p, i) => ({ ...p, seq: seqs[i] }))];
  notify(
    items.length === 1 ? `"${items[0]?.entry.label}" wasn't saved: ${items[0]?.message} It's kept for you to review.` : `${items.length} changes weren't saved and are kept for you to review.`,
    "warn",
    { label: "Review", run: () => (dialog.value = "proposals") },
    0,
  );
}

async function pump(): Promise<void> {
  if (sending || stopped) return;
  if (!connected) {
    void resync();
    return;
  }
  const batch = pending.value.filter((e) => e.seq !== undefined && e.tab === tabId && !e.held).slice(0, 25);
  if (!batch.length) return updateStatus();
  if (show.value.lock) {
    status.value = { kind: "error", message: `${show.value.lock} Your ${batch.length} unsaved change${batch.length === 1 ? " is" : "s are"} kept on this device.`, problems: [] };
    return;
  }
  sending = true;
  if (status.value.kind !== "offline") status.value = { kind: "saving" };
  let body: { results: Result[]; revision: number; generation: number };
  try {
    body = await post(`/api/shows/${show.value.showId}/entries`, { account: show.value.accountId, entries: batch.map((e) => ({ id: e.id, generation: e.generation, label: e.label, ops: e.ops })) });
  } catch (e) {
    sending = false;
    await handleSendError(e);
    return;
  }
  backoff = 1000;
  await settle(batch, body.results);
  sending = false;
  // A newer generation or revision than we hold: fetch the current book before sending more.
  if (body.generation !== show.value.generation || body.revision > show.value.revision) await resync();
  else if (pending.value.some((e) => e.tab === tabId && !e.held)) void pump();
  else updateStatus();
}

/** Apply a batch's per-entry results: acknowledged ones join the base, refused ones go to review. */
async function settle(batch: PendingEntry[], results: Result[]): Promise<void> {
  const byId = new Map(results.map((r) => [r.id, r]));
  const acked: PendingEntry[] = [];
  const refused: Proposal[] = [];
  let revision = show.value.revision;
  for (const e of batch) {
    const r = byId.get(e.id);
    if (!r) continue;
    if (r.status === "accepted" || r.status === "duplicate") {
      acked.push(e);
      revision = Math.max(revision, r.revision);
    } else if (r.status === "conflict") refused.push({ entry: e, reason: r.reason, message: r.message, records: r.records, problems: [], at: Date.now() });
    else if (r.status === "invalid") refused.push({ entry: e, reason: "invalid", message: "The server couldn't accept it.", records: [], problems: r.problems, at: Date.now() });
    else if (r.status === "held") refused.push({ entry: e, reason: "restored", message: r.message, records: [], problems: [], at: Date.now() });
  }
  if (acked.length) {
    // Only safe when every acknowledged revision is contiguous with what we hold; otherwise the
    // reply is followed by a fresh snapshot (see pump), and the base is rebuilt from the server.
    const d = structuredClone(serverDoc.value);
    if (d) for (const e of acked) for (const op of e.ops) applyOp(d, op);
    serverDoc.value = d;
    const ackedSet = new Set(acked);
    pending.value = pending.value.filter((e) => !ackedSet.has(e));
    const contiguous = revision - show.value.revision <= acked.length;
    if (contiguous) show.value = { ...show.value, revision };
    await remember(acked.map((e) => e.seq).filter((s): s is number => s !== undefined && s >= 0));
  }
  await refuse(refused);
}

async function handleSendError(e: unknown): Promise<void> {
  if (e instanceof ConnectionError) {
    connected = false;
    status.value = { kind: "offline", message: "Can't reach q2cue" };
    scheduleRetry();
    return;
  }
  if (e instanceof AuthError) {
    connected = false;
    status.value = { kind: "error", message: "Your session has ended. Sign in again to send your changes; they stay on this device until then.", problems: [] };
    return;
  }
  if (e instanceof ApiError && e.code === "wrong-account") return wrongAccount();
  if (e instanceof ApiError) {
    const body = (e.body ?? {}) as { results?: Result[]; state?: string; role?: string };
    // Some entries may have been accepted before access changed: settle those first.
    if (body.results?.length) await settle(pending.value.filter((p) => body.results?.some((r) => r.id === p.id)), body.results);
    if (e.status === 404 || e.status === 403 || e.status === 409) {
      await resync();
      if (!show.value.lock) show.value = { ...show.value, lock: e.message };
      status.value = { kind: "error", message: `${e.message} Your unsaved changes are kept on this device; you can download them from Save status.`, problems: [] };
      return;
    }
    if (e.status === 503 || e.status >= 500) {
      status.value = { kind: "offline", message: e.message };
      scheduleRetry();
      return;
    }
    status.value = { kind: "error", message: e.message, problems: [] };
  }
}

/** Re-read the show before sending edits after an interruption. Rechecks access every time. */
export function resync(): Promise<void> {
  if (refreshing) {
    refreshAgain = true;
    return refreshing;
  }
  refreshing = (async () => {
    let r: ShowResponse;
    try {
      r = await api<ShowResponse>(`/api/shows/${show.value.showId}`);
    } catch (error) {
      connected = false;
      if (error instanceof ConnectionError) {
        status.value = { kind: "offline", message: error.message };
        scheduleRetry();
      } else if (error instanceof AuthError) {
        status.value = { kind: "error", message: "Your session has ended. Sign in again to send your changes; they stay on this device until then.", problems: [] };
      } else if (error instanceof ApiError) {
        const lock = error.status === 404 ? "You no longer have access to this show." : error.message;
        show.value = { ...show.value, lock };
        status.value = { kind: "error", message: `${lock} Anything unsaved stays on this device for you to download.`, problems: [] };
      }
      return;
    }
    if (r.show.id !== show.value.showId) return;
    if (r.accountId !== show.value.accountId) return wrongAccount();
    connected = true;
    adopt(r);
    await remember();
    await holdOldGeneration();
    status.value = { kind: "saved" };
    updateStatus();
    kick();
  })().finally(() => {
    refreshing = undefined;
    if (refreshAgain) {
      refreshAgain = false;
      void resync();
    }
  });
  return refreshing;
}

/**
 * The browser is now signed in as someone else (or this account signed out in another tab): stop
 * sending, and leave the show so this account's queue is reviewed after it signs in again.
 */
export function wrongAccount(): void {
  connected = false;
  stopped = true;
  status.value = { kind: "error", message: "This browser is now signed in with a different account. Your changes stay on this device for your account; sign in again to review them.", problems: [] };
  location.replace(`/signin?return=${encodeURIComponent(location.pathname)}`);
}

/** Queued entries made before a restore are never replayed automatically. */
async function holdOldGeneration(): Promise<void> {
  const old = pending.value.filter((e) => e.generation !== show.value.generation && !e.held);
  if (!old.length) return;
  await refuse(old.map((entry) => ({
    entry,
    reason: "restored" as const,
    message: "The owner restored this show to an earlier version after you made this change. Review it before sending it again.",
    records: [],
    problems: [],
    at: Date.now(),
  })));
}

// ---------------------------------------------------------------- live updates

interface ChangeMessage {
  type: "change";
  revision: number;
  generation: number;
  actor: { id: string; name: string };
  entryId: string | null;
  label: string;
  changes: { type: RecordType; key: string; after: AnyRecord | null }[];
}

let socket: WebSocket | null = null;
let liveBackoff = 1000;
let livePresence: { page: number; editing: string | null } = { page: 1, editing: null };

export function sendPresence(page: number, editing: string | null): void {
  livePresence = { page, editing };
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "presence", ...livePresence }));
}

function applyLive(m: ChangeMessage): void {
  const s = show.value;
  if (m.generation !== s.generation || m.revision !== s.revision + 1) {
    // Missed something (or a restore): fetch the authoritative book instead of guessing.
    void resync();
    return;
  }
  const d = structuredClone(serverDoc.value);
  if (!d) return;
  for (const c of m.changes) {
    if (c.after) applyOp(d, { op: "put", type: c.type, base: null, record: c.after } as Op);
    else applyOp(d, { op: "delete", type: c.type, base: null, key: c.key } as Op);
  }
  serverDoc.value = d;
  show.value = { ...s, revision: m.revision, title: d.project.title };
  void remember();
}

export function listen(): void {
  const open = () => {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}/api/shows/${show.value.showId}/live?tab=${tabId}`);
    socket = ws;
    ws.onopen = () => {
      liveBackoff = 1000;
      ws.send(JSON.stringify({ type: "presence", ...livePresence }));
    };
    ws.onmessage = (ev) => {
      const m = JSON.parse(String(ev.data)) as { type: string } & Record<string, unknown>;
      if (m.type === "hello" || m.type === "show") {
        const h = m as unknown as { revision: number; generation: number; state: string; role?: string };
        if (!connected || h.revision !== show.value.revision || h.generation !== show.value.generation || h.state !== show.value.state || (h.role && h.role !== show.value.role)) void resync();
        else kick();
      } else if (m.type === "role") void resync();
      else if (m.type === "change") {
        const c = m as unknown as ChangeMessage;
        // Our own entries are already applied by the reply; still advance in order.
        if (c.entryId && pending.value.some((e) => e.id === c.entryId)) return;
        applyLive(c);
      } else if (m.type === "presence") presence.value = (m.people as typeof presence.value) ?? [];
    };
    ws.onclose = (ev) => {
      if (socket === ws) socket = null;
      presence.value = [];
      if (ev.code === 4401 || ev.code === 4403 || ev.code === 4410) {
        // Revoked, signed out or deleted: recheck through HTTP, which reports the real state.
        void resync();
        if (ev.code !== 4401) return;
      }
      connected = false;
      updateStatus();
      setTimeout(open, liveBackoff);
      liveBackoff = Math.min(liveBackoff * 2, 30_000);
    };
  };
  open();
  setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send('{"type":"ping"}'), 30_000);
  addEventListener("online", () => void resync());
  addEventListener("offline", () => {
    connected = false;
    updateStatus();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void resync();
  });
}

// ---------------------------------------------------------------- start-up: replay what this device holds

const HEARTBEAT_MS = 5000;
const heartbeatKey = (tab: string) => `q2q-hb-${tab}`;

/** Load pending edits from IndexedDB: this tab's own, plus any left by tabs that are gone. */
export async function loadPending(): Promise<number> {
  const beat = () => localStorage.setItem(heartbeatKey(tabId), String(Date.now()));
  beat();
  setInterval(beat, HEARTBEAT_MS);
  // A closed tab hands its pending edits over at once (a killed tab, after its heartbeat goes stale).
  addEventListener("pagehide", () => localStorage.setItem(heartbeatKey(tabId), "0"));
  addEventListener("pageshow", beat);
  const claimOrphans = async (): Promise<number> => {
    const all = await allPending();
    const orphaned = all.filter((e) => {
      if (e.tab === tabId || e.seq === undefined) return false;
      const last = Number(localStorage.getItem(heartbeatKey(e.tab)) ?? 0);
      return Date.now() - last > 3 * HEARTBEAT_MS;
    });
    if (!orphaned.length) return 0;
    await retagPending(orphaned.map((e) => e.seq as number), tabId);
    const have = new Set(pending.value.map((e) => e.seq));
    const merged = [...pending.value, ...orphaned.filter((e) => !have.has(e.seq)).map((e) => ({ ...e, tab: tabId }))];
    // Oldest first, so edits replay in the order they were made; unsaved-to-IndexedDB entries stay last.
    pending.value = merged.sort((a, b) => (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER));
    kick();
    return orphaned.length;
  };
  const all = await allPending();
  pending.value = all.filter((e) => e.tab === tabId);
  proposals.value = await allProposals();
  const adopted = await claimOrphans();
  // A tab closed moments ago still has a fresh heartbeat; claim its edits once it goes quiet.
  setInterval(() => void claimOrphans().catch(() => {}), HEARTBEAT_MS);
  return pending.value.length || adopted;
}

/**
 * Open a show: from the server when reachable, else from this account's prepared copy. Only a
 * transport failure permits offline start-up; a signed-out or forbidden answer never does.
 */
export async function start(accountId: string, showId: string, signal?: AbortSignal): Promise<{ offline: boolean; snapshot: ShowSnapshot }> {
  show.value = { ...show.value, accountId, showId };
  try {
    const r = await api<ShowResponse>(`/api/shows/${showId}`, { signal });
    if (r.accountId !== accountId) throw new AuthError("This browser is signed in with a different account.");
    connected = true;
    adopt(r);
    await remember();
    updateStatus();
    return { offline: false, snapshot: snapshot() };
  } catch (error) {
    if (!(error instanceof ConnectionError)) throw error;
    const cached = await readSnapshot();
    if (!cached?.doc || cached.showId !== showId || cached.accountId !== accountId) throw error;
    const loaded = loadDoc(JSON.stringify(cached.doc));
    if (!loaded.doc || loaded.readOnly) throw new Error(`This device's saved show cannot be opened: ${loaded.problems.join("; ")}`);
    const migrated = { ...cached, doc: loaded.doc };
    serverDoc.value = migrated.doc;
    show.value = { accountId, showId, title: cached.title, revision: cached.revision, generation: cached.generation, state: cached.state, role: cached.role, sourceSha256: cached.sourceSha256, lock: lockFor(cached.state, cached.role) };
    status.value = { kind: "offline", message: "Can't reach q2cue; showing this device's copy" };
    return { offline: true, snapshot: migrated };
  }
}

/** Release work kept through sign-out back into the queue, after the person reviewed it. */
export async function releaseHeld(): Promise<number> {
  const held = pending.value.filter((e) => e.held);
  if (!held.length) return 0;
  const released = held.map((e) => ({ ...e, held: undefined, tab: tabId }));
  await putPending(released.filter((e) => e.seq !== undefined && e.seq >= 0));
  const ids = new Set(held.map((e) => e.id));
  pending.value = [...pending.value.filter((e) => !ids.has(e.id)), ...released];
  await holdOldGeneration();
  kick();
  return released.length;
}

export async function discardHeld(ids: ReadonlySet<string>): Promise<void> {
  const held = pending.value.filter((e) => e.held && ids.has(e.id));
  await deletePending(held.map((e) => e.seq).filter((s): s is number => s !== undefined && s >= 0));
  pending.value = pending.value.filter((e) => !e.held || !ids.has(e.id));
}

/** A JSON file of everything not yet saved: the user's last resort, always available. */
export function downloadPending(): void {
  const data = {
    q2q: "unsaved changes",
    show: show.value.title,
    showId: show.value.showId,
    revision: show.value.revision,
    generation: show.value.generation,
    savedAt: new Date().toISOString(),
    entries: pending.value.map((e) => ({ id: e.id, label: e.label, at: new Date(e.at).toISOString(), generation: e.generation, ops: e.ops, before: e.before })),
    proposals: proposals.value.map((p) => ({ label: p.entry.label, reason: p.reason, message: p.message, ops: p.entry.ops, before: p.entry.before, current: p.records })),
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `q2q-unsaved-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

